/**
 * 手牌结束后的「全员确认才开下一局」与结算浮层的纯派生。
 *
 * ── 为什么不再 3 秒自动开下一手 ─────────────────────────────
 * 三秒看不清谁赢了、赢的是什么牌型。改成：在座真人全部点「继续」就立刻开下一手，
 * AI 自动算已确认，断线 / 坐出的人不计入名单，房主可强制开始，90 秒兜底。
 *
 * ★ 90 秒**不是催人**，是防「等一个已经走了、但系统以为还在的人」。
 *   所以文案里绝不能出现秒级跳动的紧迫倒计时（见 `autoStartText`）。
 *
 * ★ 界面必须说出**在等谁**：只给一个不动的「2/4」，大家除了干等什么也做不了。
 *
 * 零 React、零 DOM、零 store —— 全部可在 node 环境单测。
 */
import type { CardId, Millis, PlayerView, SeatIndex } from '@poker/shared'

import { handLabel } from '../table/cardFormat.js'

import { rankFromMadeHand, toCardIdsSafe, variantOfView } from './buildTableVM.js'
import { formatExact } from './topUpDerive.js'
import { seatName } from './transferDerive.js'

/** 等待名单里最多点名几个人，其余折成「和另外 N 人」。 */
const MAX_NAMED = 3

// ============================================================================
// 确认状态
// ============================================================================

/** 我在这轮确认里的位置。`none` = 不在应确认名单里（旁观 / 断线 / 坐出 / AI 座位）。 */
export type ReadyMine = 'none' | 'waiting' | 'ready'

export interface ReadyModel {
  /** 确认窗口开着吗。false 时结算浮层整个不出现 */
  readonly open: boolean
  /** 已确认人数 */
  readonly confirmed: number
  /** 应确认人数 */
  readonly total: number
  /** 还在等谁（昵称，已按上限折叠前的全量） */
  readonly waitingNames: readonly string[]
  readonly mine: ReadyMine
  /** 房主的逃生口 */
  readonly canForce: boolean
  /** 兜底时刻（服务端时钟） */
  readonly deadline: Millis
}

const NO_READY: ReadyModel = {
  open: false,
  confirmed: 0,
  total: 0,
  waitingNames: [],
  mine: 'none',
  canForce: false,
  deadline: 0,
}

export function readyModel(view: PlayerView | null): ReadyModel {
  const state = view?.readyState
  if (view === null || state === undefined) return NO_READY
  const mySeat = view.viewer.ownedSeat
  const confirmed = state.readySeats.length
  const waiting = state.waitingSeats
  const mine: ReadyMine =
    mySeat === null
      ? 'none'
      : state.readySeats.includes(mySeat)
        ? 'ready'
        : waiting.includes(mySeat)
          ? 'waiting'
          : 'none'

  return {
    open: true,
    confirmed,
    total: confirmed + waiting.length,
    waitingNames: waiting.map((s: SeatIndex) => seatName(view, s)),
    mine,
    canForce: view.viewer.isHost,
    deadline: state.deadline,
  }
}

/**
 * 「2/4 已确认 · 等 老王、莉莉」。
 *
 * 名字最多点三个：一桌八人时把八个昵称全列出来会把浮层撑成两行半，
 * 而第四个名字之后没有人再去读。
 */
export function readyWaitText(m: ReadyModel): string {
  const head = `${m.confirmed}/${m.total} 已确认`
  if (m.waitingNames.length === 0) return head
  const named = m.waitingNames.slice(0, MAX_NAMED).join('、')
  const rest = m.waitingNames.length - MAX_NAMED
  return rest > 0 ? `${head} · 等 ${named} 和另外 ${rest} 人` : `${head} · 等 ${named}`
}

/**
 * 兜底提示。
 *
 * ★ 刻意做成**粗粒度**：按 10 秒取整，读起来是「约 90 秒后自动继续」而不是
 *   一个每秒往下跳的数字。这不是行动计时，没有任何人需要为它加快速度。
 * ★ 也**绝不画到 0**：确认齐了还要等 `interHandDelayMs` 的最短展示时长，
 *   画一个停在 0 的数字只会让人以为卡住了。
 */
export function autoStartText(remainMs: number): string {
  if (remainMs <= 10_000) return '就要开始了'
  const sec = Math.round(remainMs / 10_000) * 10
  // 90 秒以内一律按秒说：兜底本来就是 90 秒，把它说成「约 2 分钟」是在编一个更长的数
  if (sec <= 90) return `约 ${sec} 秒后自动继续`
  return `约 ${Math.round(sec / 60)} 分钟后自动继续`
}

// ============================================================================
// 结算浮层：谁赢了、什么牌型、赢了多少
// ============================================================================

export interface HandWinLine {
  readonly key: string
  readonly seat: SeatIndex
  readonly nickname: string
  /** 已分组的金额，走 `.num` 渲染 */
  readonly amount: string
  /**
   * 牌型文案。★ 一律由 `formatHand` 产出（经 `handLabel` 转发），绝不在 UI 层拼字符串。
   * null = 没有摊牌（弃牌收场 / 对方盖牌），此时**不显示**任何牌型，也不编一个出来（红线 #3）。
   */
  readonly handText: string | null
}

export interface HandShowdownLine {
  readonly key: string
  readonly seat: SeatIndex
  readonly nickname: string
  /** null = 该玩家选择盖牌；绝不从别处推断。 */
  readonly cards: readonly [CardId, CardId] | null
  readonly handText: string | null
  /** 相同牌力共享名次；盖牌或无效牌面不排名。 */
  readonly place: number | null
  /** 实际从一个或多个边池获得的总额；null = 未获派奖。 */
  readonly amount: string | null
}

export interface HandResultModel {
  readonly handNo: number
  /** 赢家行，金额多的排前面；平分池会有多行 */
  readonly lines: readonly HandWinLine[]
  /** 完整公开摊牌名单，按真实牌力排序。 */
  readonly showdown: readonly HandShowdownLine[]
  /** 自包含的公共牌；结算卡盖住牌桌时仍能完整看懂牌力。 */
  readonly board: readonly CardId[]
  /** 是不是没摊牌就收场（全员弃牌）。true 时整屏一个牌型都不出现 */
  readonly foldWin: boolean
}

/**
 * 手牌结果 → 结算浮层的行。
 *
 * ★ 全下跑马、弃牌收场这些**没有摊牌**的局面同样要出结果：它们照样有赢家和金额，
 *   只是没有牌型。少画这一屏，玩家就只能靠看筹码数字倒推刚才发生了什么。
 *
 * 金额按座位合并：一个人同时拿下主池与边池时，两行「小明 赢得 X」读起来像赢了两次。
 */
export function handResultModel(view: PlayerView | null): HandResultModel | null {
  const result = view?.hand?.result ?? null
  if (view === null || result === null) return null

  const bySeat = new Map<SeatIndex, { amount: number; handText: string | null }>()
  for (const award of result.awards) {
    const prev = bySeat.get(award.seat) ?? { amount: 0, handText: null }
    const rank = award.hand === null ? null : rankFromMadeHand(award.hand, variantOfView(view))
    const text = award.hand === null ? null : (rank === null ? award.hand.label : handLabel(rank))
    bySeat.set(award.seat, {
      amount: prev.amount + award.amount,
      handText: prev.handText ?? text,
    })
  }

  const lines = [...bySeat.entries()]
    .sort((a, b) => b[1].amount - a[1].amount || a[0] - b[0])
    .map(([seat, v]): HandWinLine => ({
      key: `${result.handId}:${seat}`,
      seat,
      nickname: seatName(view, seat),
      amount: formatExact(v.amount),
      handText: v.handText,
    }))

  const ranked = result.showdown.map((entry) => {
    if (entry.mucked) {
      return {
        entry,
        cards: null,
        handText: null,
        value: null,
      }
    }
    const cards = toCardIdsSafe(entry.cards)
    const rank = rankFromMadeHand(entry.hand, variantOfView(view))
    return {
      entry,
      cards: cards.length === 2 ? [cards[0]!, cards[1]!] as const : null,
      handText: rank === null ? entry.hand.label : handLabel(rank),
      value: rank?.value ?? null,
    }
  }).sort((a, b) => (b.value ?? -1) - (a.value ?? -1) || a.entry.revealOrder - b.entry.revealOrder)

  let place = 0
  const showdown: HandShowdownLine[] = ranked.map((item, index) => {
    if (item.value !== null && (index === 0 || item.value !== ranked[index - 1]?.value)) place = index + 1
    const won = bySeat.get(item.entry.seat)?.amount ?? 0
    return {
      key: `${result.handId}:showdown:${item.entry.seat}`,
      seat: item.entry.seat,
      nickname: seatName(view, item.entry.seat),
      cards: item.cards,
      handText: item.handText,
      place: item.value === null ? null : place,
      amount: won > 0 ? formatExact(won) : null,
    }
  })

  return {
    handNo: result.handNo,
    lines,
    showdown,
    board: toCardIdsSafe(result.board),
    foldWin: result.endedBy === 'fold',
  }
}

/**
 * 「本手结算浮层」此刻开着吗。
 *
 * ★ 抽出来是因为**买分胶囊要躲开它**：两者都锚在舞台左下角、
 *   结算卡还是满宽，于是结算一出现就把买分入口整块盖住 ——
 *   而那正好是最需要买分的时刻（下一手 10 秒后自动开始，你刚输光）。
 *   判据留在 NextHand 内部的话，TopUp 只能靠 z-index 硬压，
 *   压上去又会盖住「继续」按钮。同一个真相，一处计算。
 */
export function handEndOverlayOpen(view: PlayerView | null, settlementOpen: boolean): boolean {
  if (settlementOpen) return false
  return readyModel(view).open && handResultModel(view) !== null
}
